/**
 * RISK-IMPORT-UPLOAD
 * Drag and drop document and ask openAi to format the doc
 */

import * as XLSX from "xlsx"
import { Socket } from "socket.io-client"
import { SocketCreate, SocketEmit } from "@/utils/socketio.utils"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import LoadingModal from "@/modals/loading.modal"
import { useState } from "react"
import { toast } from "react-toastify"
import { compact, uniq } from "lodash"
import { t } from "i18next"
import { RISK_MAPPING_COLUMNS, RiskMapping } from "../model/risk.types"

interface Props {
  onUpload: Function
}

export function RisksImportUpload(props: Props) {
  // Loading in progress
  const [isLoading, setIsLoading] = useState(false)

  //Trigger function to ask openAI
  async function askGpt<T = any>(destination: string, params: unknown): Promise<T> {
    const socket: Socket = SocketCreate()
    return new Promise<T>((resolve, reject) => {
      SocketEmit(
        socket,
        destination,
        params,
        "fr",
        undefined,
        (res) => {
          try {
            resolve(JSON.parse(res))
          } catch (e) {
            reject(e)
          }
        },
        (err) => {
          setIsLoading(false)
          reject(err)
        },
        () => {}
      )
    })
  }

  // Convert Excel date number to formatted string
  function excelDateToString(excelNum: number): string {
    if (typeof excelNum !== "number" || excelNum <= 0 || excelNum >= 60000) return String(excelNum)
    const utc_days = Math.floor(excelNum - 25569)
    const utc_value = utc_days * 86400
    const date = new Date(utc_value * 1000)
    const formatted = date.toLocaleDateString("fr-FR")
    console.log("🔢 Excel date conversion:", { excelNum, utc_days, date, formatted })
    if (isNaN(date.getTime())) return String(excelNum)
    return formatted
  }

  //Format headers and ask chatGpt for a mapping
  async function getHeaders(rows: string[][]) {
    if (rows.length > 1) {
      const mapping: RiskMapping = { rows, importColumns: [], risks: [], units: [] }
      for (let i = 0; i < rows[0].length; i++) {
        const header = rows[0][i]
        const isDateColumn = String(header).toLowerCase().includes("date")
        const sample = compact(
          uniq(
            rows.slice(1).map((row) => {
              const val = row[i]
              // Convert Excel dates ONLY for date columns with large numbers
              if (isDateColumn && typeof val === "number" && val > 25000 && val < 60000) {
                return excelDateToString(val)
              }
              // Convert booleans to string for display
              return typeof val === "boolean" ? (val ? "TRUE" : "FALSE") : val
            })
          ).slice(0, 3)
        )

        // Filtrer les colonnes vides (header vide ET aucune donnée)
        if ((header && String(header).trim() !== "") || sample.length > 0) {
          mapping.importColumns.push({
            name: header || `Column ${i + 1}`,
            sample,
            mapping: null
          })
        }
      }

      //Ask gpt for a mapping
      const response = await askGpt("risks-import", { importColumns: mapping.importColumns })

      //If response return mapping proposition
      if (response) {
        const normalize = (s: string) =>
          s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim()

        const matchedKeys = new Set<string>()

        // First pass: apply GPT matches
        mapping.importColumns.forEach((column) => {
          const matched = Object.keys(response).find((k) => response[k as keyof typeof response] === column.name)
          if (matched) {
            column.mapping = matched
            matchedKeys.add(matched)
          }
        })

        // Second pass: client-side fallback for unmatched columns
        mapping.importColumns.forEach((column) => {
          if (!column.mapping) {
            const normalizedName = normalize(column.name)
            const clientMatch = RISK_MAPPING_COLUMNS.find(
              (col) => !matchedKeys.has(col.id) && normalize(t(col.text)) === normalizedName
            )
            if (clientMatch) {
              column.mapping = clientMatch.id
              matchedKeys.add(clientMatch.id)
            } else {
              column.mapping = column.name
              column.isCustom = true
            }
          }
        })

        props.onUpload(mapping)
      } else {
        toast("Erreur serveur, veuillez réessayer plus tard.", { type: "error" })
      }

      setIsLoading(false)
    } else {
      toast("Le classeur ne contient pas de valeurs ou le format n'est pas valide.", { type: "error" })
    }
  }

  // Extract XLSX file and return rows
  async function getRows(file: File) {
    const data = await file.arrayBuffer()

    // Parse Excel workbook from buffer
    const workbook = XLSX.read(data)

    // Focus on last sheet (for multi-sheet files, import is on the last sheet)
    const sheetName = workbook.SheetNames[workbook.SheetNames.length - 1]
    const worksheet = workbook.Sheets[sheetName]

    // Convert sheet to array of arrays (rows/columns)
    let sheetData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
      header: 1, // "1" = return array of arrays instead of objects
      defval: "" // use empty string for empty cells
    })

    // Clean data (empty values)
    sheetData = sheetData.filter((row: any[]) =>
      row.some((cell) => {
        if (cell === null || cell === undefined) return false
        if (typeof cell === "string") return cell.trim() !== ""
        if (typeof cell === "number") return cell !== 0
        return true
      })
    )

    return sheetData
  }

  //Trigger upload of the file
  async function upload(files: File[]) {
    setIsLoading(true)

    if (!files.length) {
      toast("Le fichier est vide", { type: "error" })
    } else {
      //Get rows from the file
      const rows: string[][] = await getRows(files[0])

      //Format headers with gpt help
      getHeaders(rows)
    }
  }

  return (
    <div className="p-4">
      {isLoading && <LoadingModal owlAssistantText="Veillez patienter pendant que j'importe votre document unique." />}

<div className="m-[2px] mb-20">
        <b>Le document à importer est au format Excel.</b> Chaque ligne correspond à un risque identifié dans votre
        organisation.
      </div>

      <UploadButtonWidget onUpload={(items) => upload(items)} maxSize={20000000} acceptedFiles=".xlsx" />
    </div>
  )
}
